import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ContactUsFlow from './mock_sign_flow/ContactUsFlow';
import trackingService from '../services/tracking.service';
import '../styles/LandingPageKW.scss';
import '../styles/AIKeywordsLandingPage.scss';

const CORE_PILLARS = [
  {
    id: 'fanout',
    badge: 'Bulk',
    badgeLevel: 'high',
    title: 'Extract fan out queries at bulk',
    description:
      'Run one input across multiple top AI LLMs and collect the full fan out query list they use for web retrieval.',
    points: [
      'Batch query extraction',
      'Cross-model query coverage',
      // 'Unified view of prompt-to-search chains'
    ]
  },
  {
    id: 'injection',
    badge: 'Instant',
    badgeLevel: 'emerging',
    title: 'Inject content and validate citations',
    description:
      'Inject a result into the SERP, re-run the prompt, and instantly verify whether AI picks up and cites that injected page in its answer.',
    points: [
      'Instant product page test',
      'Citation visibility checks',
      'Test multiple product page variants'
    ]
  },
  {
    id: 'variants',
    badge: 'Parallel',
    badgeLevel: 'mixed',
    title: 'Run multiple prompt variants',
    description:
      'People\'s promtps differ and AI generates different fan out queries. Test intent shifts, phrasing changes, and audience framing in parallel to see how often you appear in answers.',
    points: [
      'Test many prompt variations in parralel',
      'See how often you appear in the asnwers'
    ]
  },
  {
    id: 'optimize',
    badge: 'Actionable',
    badgeLevel: 'medium',
    title: 'Optimize for AI citation',
    description:
      'Convert query and citation feedback into clear content updates so your pages are easier for AI systems to retrieve and trust.',
    points: [
      'Easily iterate and create new content versions',
      'Test instantly, without having to deploy your product page',
    ]
  }
];

const HERO_ROTATING_SUBTITLES = [
  'Automatically extract fan out queries from top AI LLMs at bulk.',
  'Inject a SERP result and instantly see if AI cites that page in the answer.',
  'Run multiple prompt variants in parallel and where and how often you appear in the answers.',
  'Iterate quickly without editing your product page'
];

// const WORKFLOW_STEPS = [
//   {
//     id: 'step-1',
//     title: 'Prompt',
//     text: 'Choose any AI and ask anything'
//   },
//   {
//     id: 'step-2',
//     title: 'Fan Out Keywords',
//     text: 'Collect real fan out queries the AI uses to search the web.'
//   },
//   {
//     id: 'step-3',
//     title: 'Content',
//     text: 'Use the collected queries to improve your content.'
//   },
//   {
//     id: 'step-4',
//     title: 'SERP Injection',
//     text: 'See how the search engine sees your content for any query. Extract and inject your content in the search results and see if AI picks it up.'
//   },
//   {
//     id: 'step-5',
//     title: 'Bulk',
//     text: 'Run multi-model multi-prompt bulk fan out search.'
//   },
//   {
//     id: 'step-5',
//     title: 'Bulk',
//     text: 'Run multi-model multi-prompt bulk fan out search.'
//   },
// ];

const AI_SEARCH_OPTIMIZATION_BLOCKS = [
  {
    id: 'search-queries',
    title: 'Extract the exact search queries AI uses',
    paragraphs: [
      'Type in any prompt and see the exact search queries the AI used, together with the search result pages (SERP). Quickly see what got cited in the answer, and what was discarded.'
    ],
    image: 'https://ik.imagekit.io/95ghwbrap/img1.png?updatedAt=1772387377169',
    imageAlt: 'Placeholder preview for extracted AI search queries'
  },
  {
    id: 'search-results',
    title: 'Who appeared in the search results?',
    paragraphs: [
      'Click on any search query to open a detailed list of all search results returned for that query, together with the chunked content extracted by the search engine.'
    ],
    image: 'https://ik.imagekit.io/95ghwbrap/img2.png?updatedAt=1772387377122',
    imageAlt: 'Placeholder preview for search result breakdown'
  },
  {
    id: 'content-injection',
    title: 'Inject your content in the search results',
    paragraphs: [
      'Will the AI cite your content? Inject it and see.',
      'We use a custom search engine that allows you to immediately index your page for any given search query. You can then inject your page in the result list for similar queries.',
      'Do this to see if the AI will select your content in its answer.'
    ],
    image: 'https://ik.imagekit.io/95ghwbrap/img3.png?updatedAt=1772387377192',
    imageAlt: 'Placeholder preview for injected content citation check'
  }
];

const AI_CONTENT_OPTIMIZATION_BLOCKS = [
  {
    id: 'optimize-for-ai-use',
    title: 'Optimize content for AI use',
    paragraphs: [
      'Create different versions of your content or product pages and instantly test how the AI interprets them.',
      'Use our internal editor, to quickly test new content versions without having to deploy your product page.',
      'Publish only the best content version to your product page.'
    ],
    image: 'https://ik.imagekit.io/95ghwbrap/img4.png?updatedAt=1772387377658',
    imageAlt: 'Placeholder preview for extracted AI search queries'
  },
  {
    id: 'instantly-index-content',
    title: 'Instanly index your page and created content',
    paragraphs: [
      'Don\'t wait for Google / Bing to index your product page. Instantly index your pages and test them immediatelly using Novenya.',
      'Just paste your URL and we will index it for any search query. You immediatelly see the SERP result, and can instantly change your content to optimize indexing.',
    ],
    image: 'https://ik.imagekit.io/95ghwbrap/img5.png?updatedAt=1772387377688',
    imageAlt: 'Placeholder preview for search result breakdown'
  },
  {
    id: 'does-ai-cite-you',
    title: 'How AI cites your content? Does it recommend your product?',
    paragraphs: [
      'See the ways your content page was used in the AI\'s answer.',
      'See what your product was recommended for. Was it the first recommendation or the last?',
      'Iterate and improve your ranking.'
    ],
    image: 'https://ik.imagekit.io/95ghwbrap/img6.png?updatedAt=1772387377762',
    imageAlt: 'Placeholder preview for injected content citation check'
  }
];

const AIKeywordsLandingPage = () => {
  const { user } = useAuth();
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [showContactUsFlow, setShowContactUsFlow] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const trackedVisibleImagesRef = useRef(new Set());

  useEffect(() => {
    const rotateInterval = setInterval(() => {
      setCurrentPhraseIndex((prev) => (prev + 1) % HERO_ROTATING_SUBTITLES.length);
    }, 4000);

    return () => clearInterval(rotateInterval);
  }, []);

  useEffect(() => {
    if (!fullscreenImage) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setFullscreenImage(null);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [fullscreenImage]);

  useEffect(() => {
    const imageTargets = document.querySelectorAll('.ai-kw-image-trigger[data-image-key]');
    if (!imageTargets.length || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const imageKey = entry.target.getAttribute('data-image-key');
          const section = entry.target.getAttribute('data-section') || '';
          const blockId = entry.target.getAttribute('data-block-id') || '';
          const imageTitle = entry.target.getAttribute('data-image-title') || '';

          if (!imageKey || trackedVisibleImagesRef.current.has(imageKey)) return;

          trackedVisibleImagesRef.current.add(imageKey);
          observer.unobserve(entry.target);

          trackingService.trackEvent('ai_keywords_image_visible', {
            additionalData: {
              page: 'ai_keywords_landing',
              image_key: imageKey,
              image_title: imageTitle,
              section,
              block_id: blockId
            }
          }).catch(() => {});
        });
      },
      {
        threshold: 0.45
      }
    );

    imageTargets.forEach((target) => observer.observe(target));

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleScrollToHowItWorks = (event) => {
    trackingService.trackButtonClick('ai_keywords_how_it_works', {
      page: 'ai_keywords_landing',
      section: 'hero'
    });

    event.preventDefault();
    const element = document.getElementById('ai-keywords-how-it-works');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleContactUsClick = (section) => {
    trackingService.trackButtonClick('ai_keywords_contact_us', {
      page: 'ai_keywords_landing',
      section
    });

    setShowContactUsFlow(true);
  };

  const handleLandingLinkClick = (linkName, destination, section) => {
    trackingService.trackLinkClick(linkName, destination, {
      page: 'ai_keywords_landing',
      section
    });
  };

  const trackImageClick = (source, section, blockId, imageTitle) => {
    trackingService.trackButtonClick('ai_keywords_image_click', {
      page: 'ai_keywords_landing',
      source,
      image_key: `${section}:${blockId}`,
      image_title: imageTitle,
      section,
      block_id: blockId
    });
  };

  const openFullscreenImage = (imageSrc, imageAlt, section, blockId, imageTitle) => {
    trackImageClick('page', section, blockId, imageTitle);

    setFullscreenImage({
      src: imageSrc,
      alt: imageAlt,
      section,
      blockId,
      imageTitle
    });
  };

  return (
    <div className="landing-kw ai-keywords-landing-v2">
      <section className="kw-hero">
        <div className="kw-hero-content">
          <div className="kw-hero-copy">
            <div className="kw-hero-main">
              <div className="kw-hero-main-content">
                <h1 className="kw-hero-main-title">Win AI search</h1>
                <div className="ai-kw-hero-gap" />
                <div className="kw-hero-main-subtitle-wrapper">
                  {HERO_ROTATING_SUBTITLES.map((subtitle, index) => (
                    <p
                      key={subtitle}
                      className={`kw-hero-main-subtitle kw-hero-rotating-phrase ${currentPhraseIndex === index ? 'active' : ''}`}
                    >
                      {subtitle}
                    </p>
                  ))}
                </div>
                <div className="ai-kw-hero-gap-bottom" />
                <div className="kw-hero-main-actions">
                  {/* <Link
                    to="/register"
                    className="kw-btn kw-btn-primary kw-btn-hero"
                    onClick={() => handleLandingLinkClick('ai_keywords_start_free_hero', '/register', 'hero')}
                  >
                    Start free
                  </Link>
                  <button onClick={handleScrollToHowItWorks} className="kw-btn kw-btn-ghost kw-btn-hero">
                    How does it work?
                  </button> */}
                  <button
                    type="button"
                    className="kw-btn kw-btn-ghost kw-btn-hero kw-btn-contact"
                    onClick={() => handleContactUsClick('hero')}
                  >
                    Contact us
                  </button>
                </div>
              </div>
            </div>

            <div className="kw-hero-steps" id="ai-keywords-how-it-works">
              <h2 className="kw-hero-steps-title">Optimize your content and products to be recommended by AIs</h2>
              <p className="ai-kw-steps-intro">
                Extract what AI actually searches, run controlled prompt experiments, and instantly validate whether injected SERP content gets cited in the final answer.
              </p>
              {/* <div className="ai-kw-step-grid">
                {WORKFLOW_STEPS.map((step, index) => (
                  <article key={step.id} className="kw-hero-step">
                    <div className="kw-hero-step-badge">{index + 1}</div>
                    <div className="kw-hero-step-body">
                      <h3 className="kw-hero-step-title">{step.title}</h3>
                      <p className="kw-hero-step-text">{step.text}</p>
                    </div>
                  </article>
                ))}
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* <div style={{marginTop: '15rem'}}></div> */}

      <section className="ai-kw-optimization-content">
        <fieldset className="kw-demo ai-kw-optimization-fieldset">
          <legend className="kw-demo-legend">AI SEARCH OPTIMIZATION</legend>
          <div className="ai-kw-optimization-list">
            {AI_SEARCH_OPTIMIZATION_BLOCKS.map((block) => (
              <article key={block.id} className="ai-kw-optimization-row">
                <div className="ai-kw-optimization-copy">
                  <h3>{block.title}</h3>
                  {block.paragraphs.map((paragraph) => (
                    <p key={`${block.id}-${paragraph}`}>{paragraph}</p>
                  ))}
                </div>
                <div className="ai-kw-optimization-image-wrap">
                  <button
                    type="button"
                    className="ai-kw-image-trigger"
                    data-image-key={`ai_search_optimization:${block.id}`}
                    data-section="ai_search_optimization"
                    data-block-id={block.id}
                    data-image-title={block.title}
                    onClick={() => openFullscreenImage(block.image, block.imageAlt, 'ai_search_optimization', block.id, block.title)}
                    aria-label={`Open image preview: ${block.title}`}
                  >
                    <img src={block.image} alt={block.imageAlt} className="ai-kw-optimization-image" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </fieldset>
      </section>

      <div style={{marginTop: '15rem'}}></div>

      <section className="ai-kw-optimization-content">
        <fieldset className="kw-demo ai-kw-optimization-fieldset">
          <legend className="kw-demo-legend">OPTIMIZE CONTENT FOR AI CITATION</legend>
          <div className="ai-kw-optimization-list">
            {AI_CONTENT_OPTIMIZATION_BLOCKS.map((block) => (
              <article key={block.id} className="ai-kw-optimization-row">
                <div className="ai-kw-optimization-copy">
                  <h3>{block.title}</h3>
                  {block.paragraphs.map((paragraph) => (
                    <p key={`${block.id}-${paragraph}`}>{paragraph}</p>
                  ))}
                </div>
                <div className="ai-kw-optimization-image-wrap">
                  <button
                    type="button"
                    className="ai-kw-image-trigger"
                    data-image-key={`ai_content_optimization:${block.id}`}
                    data-section="ai_content_optimization"
                    data-block-id={block.id}
                    data-image-title={block.title}
                    onClick={() => openFullscreenImage(block.image, block.imageAlt, 'ai_content_optimization', block.id, block.title)}
                    aria-label={`Open image preview: ${block.title}`}
                  >
                    <img src={block.image} alt={block.imageAlt} className="ai-kw-optimization-image" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </fieldset>
      </section>

      <div style={{marginTop: '15rem'}}></div>

      <section className="kw-intents">
        <div className="kw-section-header">
          <div>
            <h2>Core capabilities</h2>
            <p>Get cited by AI. Improve how you appear in the AI's answers.</p>
          </div>
        </div>
        <div className="kw-intents-grid">
          {CORE_PILLARS.map((pillar) => (
            <article
              key={pillar.id}
              className={`kw-intent-card ${pillar.id === 'injection' ? 'kw-intent-highlight' : ''}`}
            >
              <div className="kw-intent-head">
                <h3>{pillar.title}</h3>
                <span className={`kw-intent-level ${pillar.badgeLevel}`}>{pillar.badge}</span>
              </div>
              <p className="kw-intent-sub">{pillar.description}</p>
              <div className="kw-intent-meta">
                <span>What you can do</span>
                <ul>
                  {pillar.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="kw-cta">
        <div>
          <h2>Build pages AI systems choose to cite.</h2>
          <p>Move from guesswork to measurable citation outcomes with repeatable prompt and content testing.</p>
        </div>
        <div className="kw-cta-actions">
          {/* <Link
            to="/ai-keywords"
            className="kw-btn kw-btn-primary"
            onClick={() => handleLandingLinkClick('ai_keywords_start_now_free', '/ai-keywords', 'bottom_cta')}
          >
            Start Now For Free
          </Link>
          <Link
            to="/register"
            className="kw-btn kw-btn-ghost"
            onClick={() => handleLandingLinkClick('ai_keywords_create_account', '/register', 'bottom_cta')}
          >
            Create account
          </Link> */}
          <button
            type="button"
            className="kw-btn kw-btn-ghost kw-btn-contact"
            onClick={() => handleContactUsClick('bottom_cta')}
          >
            Contact us
          </button>
        </div>
      </section>
      <ContactUsFlow
        isOpen={showContactUsFlow}
        onClose={() => setShowContactUsFlow(false)}
        initialEmail={user?.email || ''}
        plan={{ id: 'enterprise', name: 'Enterprise', price: 'Contact us', period: '' }}
      />
      {fullscreenImage && (
        <div
          className="ai-kw-image-lightbox"
          onClick={() => setFullscreenImage(null)}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              setFullscreenImage(null);
            }
          }}
        >
          <button
            type="button"
            className="ai-kw-image-lightbox-close"
            onClick={(event) => {
              event.stopPropagation();
              setFullscreenImage(null);
            }}
            aria-label="Close full screen image"
          >
            ×
          </button>
          <div className="ai-kw-image-lightbox-content" onClick={(event) => event.stopPropagation()}>
            <img
              src={fullscreenImage.src}
              alt={fullscreenImage.alt}
              className="ai-kw-image-lightbox-image"
              onClick={() => trackImageClick('fullscreen', fullscreenImage.section, fullscreenImage.blockId, fullscreenImage.imageTitle)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AIKeywordsLandingPage;
